import { progressiveCellArtwork } from './67-progressive-puzzles.mjs';
import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import { gameCellArtwork, paintSceneArtwork, brookArtworkKey } from './36-game-artwork.mjs';
import { visibleMemoryName } from './49-score-artwork.mjs';
import { ribbonArtworkKey } from './45-ribbon-artwork.mjs';

const shapes = ['●', '◆', '✦', '▲', '⬟', '✚'];
const colourNames = ['Teal', 'Coral', 'Violet', 'Gold', 'Blue', 'Pink'];
/** Token-owned colour, visible silhouette and spoken name share one identity. */
export function gameColourPresentation(index) {
  return { shape: shapes[index], name: colourNames[index], colour: `var(--game-colour-${index})` };
}
function pieceText(state, cell, index) {
  if (cell.glyph) return cell.glyph;
  if (state.gameId === 'memory-pairs' && cell.value === null) return '✧';
  if (state.gameId === 'block-fit') return cell.value ? '■' : '';
  if (state.gameId === 'colour-path') return cell.endpoint ? shapes[cell.colour % 6] : '';
  if (['mosaic-paint', 'pattern-stamp', 'paper-fold'].includes(state.gameId))
    return cell.value < 0 ? '' : shapes[cell.value % 6];
  if (['ripple-garden', 'cloud-shapes', 'thread-loom'].includes(state.gameId)) return '';
  if (['tile-slide', 'soft-merge'].includes(state.gameId) && cell.value === 0) return '';
  return cell.value === undefined ? shapes[index % shapes.length] : String(cell.value);
}

function layers(ctx, state, cell) {
  const vessel = ctx.e('span', 'game-piece game-vessel');
  vessel.setAttribute('aria-hidden', 'true');
  vessel.dataset.capacity = String(cell.capacity || 4);
  if (state.gameId === 'sand-layers') vessel.classList.add('game-sand-vessel');
  vessel.dataset.complete = String(
    cell.layers.length === (cell.capacity || 4) &&
      cell.layers.every((colour) => colour === cell.layers[0]),
  );
  for (const [index, colour] of cell.layers.entries()) {
    const layer = ctx.e(
      'span',
      state.gameId === 'sand-layers' ? 'game-layer game-sand-layer' : 'game-layer game-marble',
      shapes[colour % shapes.length],
    );
    layer.style.setProperty('--piece-colour', `var(--game-colour-${colour})`);
    layer.style.setProperty('--layer-index', String(index));
    layer.dataset.colour = String(colour);
    vessel.append(layer);
  }
  return vessel;
}

function setCellMetadata(button, cell, state) {
  for (const name of ['selected', 'destination', 'blocked', 'lit', 'matched', 'target', 'endpoint'])
    button.dataset[name] = String(Boolean(cell[name]));
  button.dataset.empty = String(
    cell.value === null ||
      cell.value === '' ||
      (['tile-slide', 'soft-merge'].includes(state.gameId) && cell.value === 0),
  );
  button.dataset.neutral = String(!Number.isInteger(cell.colour));
  if (Number.isInteger(cell.path)) button.dataset.path = String(cell.path);
  else if (typeof cell.path === 'boolean') button.dataset.path = String(cell.path);
  else delete button.dataset.path;
  if (cell.previewCells) button.dataset.previewCells = cell.previewCells.join(',');
  else delete button.dataset.previewCells;
  if (Number.isInteger(cell.checkpoint)) button.dataset.checkpoint = String(cell.checkpoint);
  else delete button.dataset.checkpoint;
  if (Number.isInteger(cell.side)) button.dataset.side = String(cell.side);
  else delete button.dataset.side;
  button.style.setProperty(
    '--piece-colour',
    Number.isInteger(cell.colour) ? `var(--game-colour-${cell.colour})` : 'var(--game-neutral)',
  );
}

/** Native keyed buttons own input, focus and descriptions; SVG/CSS own artwork.
 * The renderer consumes derived view metadata, never hidden memory answers or
 * puzzle witnesses. It leaves vertical scrolling native and exposes legal
 * destinations before the next action instead of communicating only errors. */
export function paintGameBoard(
  ctx,
  host,
  state,
  move,
  { disabled = false, motion = true, render = true } = {},
) {
  const view = gameView(state);
  if (!render) return view;
  const previous = host.gameState ? gameView(host.gameState) : null;
  host.style.setProperty('--game-columns', String(view.columns));
  host.dataset.game = state.gameId;
  host.dataset.family = view.family || state.gameId;
  if (state.gameId === 'mosaic-paint') {
    const brush = view.options.find((option) => option.selected && Number.isInteger(option.colour));
    if (brush) host.style.setProperty('--mosaic-ink', `var(--game-colour-${brush.colour})`);
  }
  const existing = new Map(
    [...host.children]
      .filter((node) => node.dataset.cell !== undefined)
      .map((node) => [node.dataset.cell, node]),
  );
  const desired = view.cells.map((cell, index) => {
    let button = existing.get(String(index));
    if (!button) {
      button = ctx.e('button', 'game-cell');
      button.type = 'button';
      button.dataset.cell = String(index);
      button.addEventListener('click', () => {
        const current = gameView(host.gameState).cells[index];
        if (!button.disabled && current?.move && !current.disabled) move(current.move);
      });
    }
    button.disabled = disabled || Boolean(cell.disabled) || !cell.move;
    const character = state.gameId === 'memory-pairs' ? visibleMemoryName(cell) : null;
    button.setAttribute(
      'aria-label',
      character
        ? `${character} memory card${cell.matched ? ', matched' : ''}`
        : state.gameId === 'cluster-pop'
          ? `${cell.label} · Row ${Math.floor(index / view.columns) + 1}, column ${(index % view.columns) + 1} · ${cell.clusterSize} connected · ${cell.previewScore} points`
          : cell.label,
    );
    button.setAttribute('aria-pressed', String(Boolean(cell.selected)));
    setCellMetadata(button, cell, state);
    if (state.gameId === 'ball-sort') {
      button.dataset.tubeLabel = String(index + 1).padStart(2, '0');
      button.style.setProperty(
        '--marble-column',
        String(
          (index % view.columns) * 2 +
            1 +
            (index >= view.columns ? view.columns * 2 - view.cells.length : 0),
        ),
      );
    }
    // Sprite identity survives selection, disabled-state and unchanged phase
    // repaints, avoiding fresh gem decodes/bubble SVGs and selection-only entrance pulses.
    const samePieceAppearance =
      [
        'gem-swap',
        'cluster-pop',
        'block-fit',
        'memory-pairs',
        'number-trail',
        'mosaic-paint',
        'spot-pair',
      ].includes(state.gameId) &&
      host.gameState?.gameId === state.gameId &&
      previous?.cells[index]?.value === cell.value &&
      previous?.cells[index]?.matched === cell.matched &&
      previous?.cells[index]?.colour === cell.colour &&
      previous?.cells[index]?.special === cell.special &&
      previous?.cells[index]?.glyph === cell.glyph &&
      previous?.cells[index]?.target === cell.target &&
      (state.gameId !== 'number-trail' || previous?.cells[index]?.selected === cell.selected) &&
      Boolean(button.firstChild);
    const sameRibbonAppearance =
      state.gameId === 'colour-path' &&
      host.gameState?.gameId === state.gameId &&
      previous?.cells[index] &&
      Boolean(button.firstChild) &&
      ribbonArtworkKey(host.gameState, previous.cells[index], index) ===
        ribbonArtworkKey(state, cell, index);
    const sameBrookAppearance =
      state.gameId === 'flow-paths' &&
      host.gameState?.gameId === state.gameId &&
      previous?.cells[index] &&
      Boolean(button.firstChild) &&
      brookArtworkKey(host.gameState, previous.cells[index], index) ===
        brookArtworkKey(state, cell, index);
    const sameMarbleAppearance =
      state.gameId === 'ball-sort' &&
      host.gameState?.seed === state.seed &&
      JSON.stringify(previous?.cells[index]?.layers) === JSON.stringify(cell.layers) &&
      Boolean(button.firstChild);
    const retainArtwork =
      samePieceAppearance || sameRibbonAppearance || sameBrookAppearance || sameMarbleAppearance;
    let artwork;
    if (retainArtwork) artwork = button.firstChild;
    else if (['ball-sort', 'pebble-balance'].includes(state.gameId))
      artwork = progressiveCellArtwork(ctx, state, cell);
    else if (cell.layers && state.gameId !== 'sand-layers') artwork = layers(ctx, state, cell);
    else {
      artwork = ctx.e('span', 'game-piece');
      artwork.setAttribute('aria-hidden', 'true');
      const shape = shapes[(cell.colour ?? cell.value ?? index) % shapes.length];
      artwork.append(
        gameCellArtwork(ctx, state, cell, index, shape, pieceText(state, cell, index)),
      );
      if (state.gameId === 'gem-swap')
        artwork.append(ctx.e('span', 'game-material-symbol', cell.glyph || shape));
    }
    if (!retainArtwork) button.replaceChildren(artwork);
    button.dataset.power = cell.special || 'ordinary';
    if (cell.special && state.gameId !== 'gem-swap') {
      const badge = ctx.e(
        'span',
        'game-prism-badge',
        { row: '↔', col: '↕', burst: '✹' }[cell.special],
      );
      badge.setAttribute('aria-hidden', 'true');
      button.append(badge);
    }
    if (
      previous &&
      ![
        'colour-path',
        'flow-paths',
        'spot-pair',
        'mosaic-paint',
        'pattern-stamp',
        'cloud-shapes',
        'thread-loom',
        'paper-fold',
        'pebble-balance',
      ].includes(state.gameId) &&
      !retainArtwork &&
      motion &&
      host.isConnected &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      JSON.stringify(previous.cells[index]) !== JSON.stringify(cell) &&
      !cell.layers &&
      typeof artwork.animate === 'function'
    ) {
      const flip = state.gameId === 'memory-pairs' && previous.cells[index]?.value !== cell.value;
      artwork.animate(
        flip
          ? [
              { transform: 'perspective(600px) rotateY(90deg)', opacity: 1 },
              { transform: 'perspective(600px) rotateY(0deg)', opacity: 1 },
            ]
          : [
              { transform: 'scale(.93)', opacity: 0.72 },
              { transform: 'scale(1)', opacity: 1 },
            ],
        { duration: flip ? 190 : 170, easing: 'ease-out' },
      );
    }
    return button;
  });
  host.gameState = state;
  const retained = new Set(desired);
  for (const node of [...host.children]) if (!retained.has(node)) node.remove();
  let next = host.firstChild;
  for (const node of desired) {
    if (node !== next) host.insertBefore(node, next);
    next = node.nextSibling;
  }
  return view;
}

/** Paint a complete noninteractive SVG family scene under the native surface.
 * No frame loop or GPU resources are allocated here; optional Three.js belongs
 * to its lifecycle owner and can enhance this usable fallback. */
export function paintGameScene(ctx, host, state) {
  const view = gameView(state);
  paintSceneArtwork(ctx, host, view, state);
  return view;
}

const activePours = new WeakMap();

/** Release presentation-only marble flights before reset, disposal or suspension.
 * Accepted domain volumes are already painted and never changed by an effect. */
export function cancelPour(board) {
  activePours.get(board)?.cancel();
}

/** Capture every outgoing marble before an immutable accepted run transfer.
 * Geometry includes the selected vessel's lift, so the flight starts where the
 * player saw the marble rather than an estimated grid coordinate. */
export function capturePour(board, state) {
  cancelPour(board);
  if (
    state.gameId !== 'ball-sort' ||
    state.data.selected < 0 ||
    document.hidden ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
    return null;
  const source = state.data.selected;
  const drag = board.gameDragPour;
  if (drag?.source === source && drag.seed === state.seed && drag.marbles.length)
    return { source, marbles: drag.marbles };
  const nodes = board.querySelectorAll(`[data-cell="${source}"] .game-layer`);
  return {
    source,
    marbles: [...nodes].map((node) => ({
      rect: node.getBoundingClientRect(),
      node: node.cloneNode(true),
    })),
  };
}

/** Show each accepted marble leaving, arcing and landing in the real receiver.
 * The final board remains authoritative. Cancellation, hidden documents and
 * reduced motion reveal that final board immediately without deferred moves. */
export function animatePour(board, previous, state, capture) {
  cancelPour(board);
  if (
    !capture ||
    state.gameId !== 'ball-sort' ||
    state.data.selected !== -1 ||
    document.hidden ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
    return null;
  const destination = state.data.tubes.findIndex(
    (tube, index) => tube.length > previous.data.tubes[index].length,
  );
  if (destination < 0) return null;
  const count = state.data.tubes[destination].length - previous.data.tubes[destination].length;
  const targets = [...board.querySelectorAll(`[data-cell="${destination}"] .game-layer`)].slice(
    -count,
  );
  const outgoing = capture.marbles.slice(-count).reverse();
  if (
    targets.length !== count ||
    outgoing.length !== count ||
    outgoing.some(({ node }) => typeof node.animate !== 'function')
  )
    return null;
  const origin = board.getBoundingClientRect();
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animations = [],
    ghosts = [];
  let cancelled = false,
    revealed = false,
    cleaned = false;
  const reveal = () => {
    if (revealed) return;
    revealed = true;
    for (const ghost of ghosts) ghost.remove();
    for (const target of targets) target.style.opacity = '';
  };
  const clean = () => {
    if (cleaned) return;
    cleaned = true;
    reveal();
    document.removeEventListener('visibilitychange', hidden);
    media.removeEventListener?.('change', suspended);
    if (activePours.get(board) === controller) activePours.delete(board);
  };
  const suspended = () => {
    if (document.hidden || media.matches) controller.cancel();
  };
  const hidden = suspended;
  const controller = {
    cancel() {
      if (cancelled) return;
      cancelled = true;
      for (const animation of animations) animation.cancel();
      clean();
    },
    finished: null,
  };
  activePours.set(board, controller);
  document.addEventListener('visibilitychange', hidden);
  media.addEventListener?.('change', suspended);
  for (const [index, target] of targets.entries()) {
    const end = target.getBoundingClientRect();
    const { rect, node: ghost } = outgoing[index];
    ghost.classList.add('game-pour-piece');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.left = `${end.left - origin.left}px`;
    ghost.style.top = `${end.top - origin.top}px`;
    ghost.style.width = `${end.width}px`;
    ghost.style.height = `${end.height}px`;
    board.append(ghost);
    ghosts.push(ghost);
    target.style.opacity = '0';
    const x = rect.left - end.left,
      y = rect.top - end.top;
    const distance = Math.hypot(x, y);
    const lift = Math.max(end.width, Math.min(90, distance * 0.22));
    const controlY = Math.min(y, 0) - lift * 2;
    // Sample one continuous arc with linear pacing. Segment-wide easing would
    // stop each marble at every control point instead of following gravity.
    const frames = Array.from({ length: 25 }, (_, sample) => {
      const t = (1 - Math.cos((Math.PI * sample) / 24)) / 2,
        remaining = 1 - t;
      const px = x * remaining;
      const py = remaining * remaining * y + 2 * remaining * t * controlY;
      const squash = sample === 23 ? 'scale(.98, 1.02)' : 'scale(1)';
      return {
        transform: `translate(${Number(px.toFixed(2))}px, ${Number(py.toFixed(2))}px) ${squash}`,
        offset: sample / 24,
      };
    });
    animations.push(
      ghost.animate(frames, {
        duration: Math.min(580, 420 + distance * 0.18),
        delay: index * 70,
        fill: 'backwards',
        easing: 'linear',
      }),
    );
  }
  controller.finished = (async () => {
    try {
      await Promise.allSettled(animations.map((animation) => animation.finished));
      reveal();
      if (cancelled || !board.isConnected || media.matches || document.hidden) return;
      const vessel = board.querySelector(`[data-cell="${destination}"] .game-vessel`);
      if (vessel?.dataset.complete === 'true' && typeof vessel.animate === 'function') {
        const halo = vessel.animate(
          [
            { filter: 'drop-shadow(0 0 3px var(--game-colour-3)) brightness(1)' },
            { filter: 'drop-shadow(0 0 14px var(--game-colour-3)) brightness(1.12)', offset: 0.35 },
            { filter: 'drop-shadow(0 0 5px var(--game-colour-3)) brightness(1)' },
          ],
          { duration: 420, easing: 'ease-out' },
        );
        animations.push(halo);
        await Promise.allSettled([halo.finished]);
      }
    } finally {
      clean();
    }
  })();
  return controller;
}
